import { useQuery } from '@tanstack/react-query';
import { getBalance } from '@wagmi/core';
import { mainnet, bsc, base, polygon, arbitrum, optimism } from 'wagmi/chains';

import { QueryOptions } from 'utils/useQueryUtils';
import { ETH_ZERO_ADDRESS } from 'utils/constants/ethConst';
import { wagmiConfig } from 'structure/providers/wagmiConfig';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';

type SupportedChainName =
  | 'ethereum'
  | 'bsc'
  | 'base'
  | 'polygon'
  | 'arbitrum'
  | 'optimism';

const CHAINS: Array<{ chainId: number; name: SupportedChainName }> = [
  { chainId: mainnet.id, name: 'ethereum' },
  { chainId: bsc.id, name: 'bsc' },
  { chainId: base.id, name: 'base' },
  { chainId: polygon.id, name: 'polygon' },
  { chainId: arbitrum.id, name: 'arbitrum' },
  { chainId: optimism.id, name: 'optimism' },
];

const EMPTY_BALANCES: ChainBalanceMap = {
  [mainnet.id]: BigInt(0),
  [bsc.id]: BigInt(0),
  [base.id]: BigInt(0),
  [polygon.id]: BigInt(0),
  [arbitrum.id]: BigInt(0),
  [optimism.id]: BigInt(0),
};

type ChainBalanceMap = Record<number, bigint>;

interface UseNativeBalancesParams extends QueryOptions {
  address?: `0x${string}`;
}

export const useNativeBalances = (params: UseNativeBalancesParams) => {
  const { address, query } = params;

  return useQuery({
    queryKey: [queryKeys.nativeBalances({ address: address as string })],
    enabled:
      Boolean(address && address !== ETH_ZERO_ADDRESS) &&
      (query?.enabled ?? true),
    queryFn: async (): Promise<ChainBalanceMap> => {
      if (!address || address === ETH_ZERO_ADDRESS) {
        return EMPTY_BALANCES;
      }

      const balances = await Promise.all(
        CHAINS.map(async ({ chainId }) => {
          const result = await getBalance(wagmiConfig, {
            address,
            chainId,
          });

          return [chainId, result.value] as const;
        })
      );

      return balances.reduce<ChainBalanceMap>((acc, [chainId, balance]) => {
        acc[chainId] = balance;
        return acc;
      }, {});
    },
    ...query,
  });
};
